import { useCallback, useMemo, useRef, useState } from 'react';

// Extracted from ChatItem.js / AliveChatItem.js. Owns the reaction-picker anchor
// ref, open/close state, dot-menu items, and dot-menu click routing shared by
// both chat item components.
export const useChatItemActions = ({
    message,
    onToggle,
    onEdit,
    onDelete,
    onShowReceipts,
    onReplyInThread,
    canEdit,
    canDelete,
    canReact = true,
    canSeeReceipts = false,
    canThread = false,
}) => {
    const actionsRef = useRef(null);
    const [pickerAnchor, setPickerAnchor] = useState(null);

    const openPicker = useCallback(() => setPickerAnchor(actionsRef.current), []);
    const closePicker = useCallback(() => setPickerAnchor(null), []);
    const handlePickerSelect = useCallback(
        emoji => {
            onToggle(emoji);
            setPickerAnchor(null);
        },
        [onToggle]
    );

    const menuItems = useMemo(
        () =>
            [
                canReact && { name: 'Add reaction', icon: 'emoticon-outline' },
                canSeeReceipts && { name: 'Read by', icon: 'check-all' },
                canThread && { name: 'Reply in Thread', icon: 'thread', iconType: 'af' },
                canEdit && { name: 'Edit', icon: 'pencil' },
                canDelete && { name: 'Delete', icon: 'delete' },
            ].filter(Boolean),
        [canReact, canSeeReceipts, canThread, canEdit, canDelete]
    );

    const onDotMenuClick = useCallback(
        title => {
            switch (title) {
                case 'Edit':
                    onEdit(true, message);
                    break;
                case 'Delete':
                    onDelete && onDelete(message);
                    break;
                case 'Add reaction':
                    openPicker();
                    break;
                case 'Read by':
                    onShowReceipts && onShowReceipts(message);
                    break;
                case 'Reply in Thread':
                    onReplyInThread && onReplyInThread(message);
                    break;
                default:
                    break;
            }
        },
        [onEdit, onDelete, onShowReceipts, onReplyInThread, message, openPicker]
    );

    return { actionsRef, pickerAnchor, openPicker, closePicker, handlePickerSelect, menuItems, onDotMenuClick };
};

export default useChatItemActions;
